import { Color } from '@precon-tools-suite-by-shared/precon-graphql-types';

export type ColorConfig = {
    cssClassName: string;
};

export type ColorRecord<T> = Record<Color, T>;

const defaultColorConfigs: ColorRecord<ColorConfig> = {
    [Color.Avocado]: {
        cssClassName: 'avocado',
    },
    [Color.Black]: {
        cssClassName: 'black',
    },
    [Color.Blue]: {
        cssClassName: 'blue',
    },
    [Color.Bluegray]: {
        cssClassName: 'bluegray',
    },
    [Color.Cyan]: {
        cssClassName: 'cyan',
    },
    [Color.Gray]: {
        cssClassName: 'gray',
    },
    [Color.Green]: {
        cssClassName: 'green',
    },
    [Color.Indigo]: {
        cssClassName: 'indigo',
    },
    [Color.Orange]: {
        cssClassName: 'orange',
    },
    [Color.Pink]: {
        cssClassName: 'pink',
    },
    [Color.Purple]: {
        cssClassName: 'purple',
    },
    [Color.Red]: {
        cssClassName: 'red',
    },
    [Color.Teal]: {
        cssClassName: 'teal',
    },
    [Color.White]: {
        cssClassName: 'white',
    },
    [Color.Yellow]: {
        cssClassName: 'yellow',
    },
};

const defaultPallet: Color[] = [
    Color.Gray,
    Color.Bluegray,
    Color.Indigo,
    Color.Blue,
    Color.Cyan,
    Color.Teal,
    Color.Green,
    Color.Avocado,
    Color.Yellow,
    Color.Orange,
    Color.Red,
    Color.Pink,
    Color.Purple,
];

/**
 * Put all the options for this library that must be set in the `main.ts` of the application here.
 */
export interface PreconColorsGlobalOptions {
    colorConfig?: Partial<ColorRecord<Partial<ColorConfig>>>;
    pallet?: Color[];
    fallbackColor?: Color;
}

/**
 * This is the type of the options object used within the library.
 */
export type NormalizedPreconColorsGlobalOptions = {
    colorConfigs: ColorRecord<ColorConfig>;
    pallet: readonly Color[];
    fallbackColor: Color;
};

/**
 * Function to convert the input options (`PreconColorsGlobalOptions`) to the normalized
 * options (`NormalizedPreconColorsGlobalOptions`).
 *
 * It usually just sets the default values for the library.
 *
 * @param options The input options as set in the application that uses this library.
 */
export function normalizePreconColorsOptions(
    options: PreconColorsGlobalOptions = {}
): NormalizedPreconColorsGlobalOptions {
    const colorConfigOverwrites = options.colorConfig ?? {};
    const colorConfigEntries: [Color, ColorConfig][] = [];
    for (const color of Object.values(Color)) {
        colorConfigEntries.push([
            color,
            {
                ...defaultColorConfigs[color],
                ...(colorConfigOverwrites[color] ?? {}),
            },
        ]);
    }

    return {
        colorConfigs: Object.fromEntries(
            colorConfigEntries
        ) as ColorRecord<ColorConfig>,
        pallet: (options.pallet ?? defaultPallet) as readonly Color[],
        fallbackColor: Color.Gray,
    };
}
